import { useState, useEffect, useCallback, useMemo, Fragment } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { toast } from 'sonner';
import { Loader2, RefreshCw, Search, History, User, Calendar, Activity, ChevronDown, ChevronRight } from 'lucide-react';

const ACTION_LABELS = {
  create: { label: 'Création', color: 'bg-green-100 text-green-700 border-green-200' },
  update: { label: 'Modification', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  delete: { label: 'Suppression', color: 'bg-red-100 text-red-700 border-red-200' },
  stock_in: { label: 'Ajout stock', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  stock_out: { label: 'Retrait stock', color: 'bg-orange-100 text-orange-700 border-orange-200' },
  rename_category: { label: 'Renommage cat.', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  delete_category: { label: 'Suppr. catégorie', color: 'bg-red-100 text-red-700 border-red-200' },
  bulk_delete: { label: 'Suppr. groupée', color: 'bg-red-100 text-red-700 border-red-200' },
  snapshot: { label: 'Snapshot', color: 'bg-slate-100 text-slate-700 border-slate-200' },
  restore: { label: 'Restauration', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  import: { label: 'Import CSV/XLSX', color: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  jete_create: { label: 'Produit jeté', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  jete_delete: { label: 'Suppr. jeté', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  jete_bulk_delete: { label: 'Suppr. jetés (groupe)', color: 'bg-amber-100 text-amber-700 border-amber-200' },
};

function fmtDateTime(s) {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return s; }
}

function todayIso() { return new Date().toISOString().slice(0, 10); }
function daysAgoIso(n) { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); }

export function HistoriqueTab() {
  const { api } = useAuth();
  const [logs, setLogs] = useState([]);
  const [synthese, setSynthese] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(daysAgoIso(7));
  const [dateTo, setDateTo] = useState(todayIso());
  const [userFilter, setUserFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { date_from: dateFrom, date_to: dateTo, limit: 500 };
      if (userFilter !== 'all') params.user_email = userFilter;
      if (actionFilter !== 'all') params.action = actionFilter;
      if (search.trim()) params.item_search = search.trim();
      const [logsRes, synthRes] = await Promise.all([
        api.get('/inventaire/audit', { params }),
        api.get('/inventaire/audit/synthese', { params: { date_from: dateFrom, date_to: dateTo } }),
      ]);
      setLogs(logsRes.data);
      setSynthese(synthRes.data);
    } catch { toast.error('Erreur chargement historique'); }
    finally { setLoading(false); }
  }, [api, dateFrom, dateTo, userFilter, actionFilter, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const renderDetails = (log) => {
    const d = log.details || {};
    if (log.action === 'stock_in' || log.action === 'stock_out') {
      return (
        <div className="text-xs space-y-1">
          <div>Stock : <b>{d.stock_avant} → {d.stock_apres}</b> {d.unite} ({log.action === 'stock_in' ? '+' : ''}{d.delta})</div>
          {d.notes && <div>Notes : {d.notes}</div>}
        </div>
      );
    }
    if (log.action === 'update') {
      const changes = d.changes || {};
      return (
        <div className="text-xs space-y-1">
          {Object.entries(changes).map(([k, v]) => (
            <div key={k}><b>{k}</b> : <span className="text-muted-foreground">{JSON.stringify(v.avant)}</span> → {JSON.stringify(v.apres)}</div>
          ))}
        </div>
      );
    }
    if (log.action === 'create') {
      return <div className="text-xs">Stock initial : <b>{d.stock_initial} {d.unite}</b> · Seuil : {d.seuil_alerte}</div>;
    }
    if (log.action === 'delete' || log.action === 'jete_delete') {
      return <div className="text-xs">Au moment de la suppression : <b>{d.stock_au_moment_suppression ?? '—'} {d.unite ?? ''}</b></div>;
    }
    if (log.action === 'rename_category') {
      return <div className="text-xs"><b>{d.ancien_nom}</b> → <b>{d.nouveau_nom}</b> ({d.articles_impactes} articles)</div>;
    }
    if (log.action === 'delete_category' || log.action === 'bulk_delete' || log.action === 'jete_bulk_delete') {
      return <div className="text-xs">{d.articles_supprimes ?? '?'} articles : <span className="text-muted-foreground">{(d.noms || []).slice(0, 8).join(', ')}{(d.noms || []).length > 8 ? '…' : ''}</span></div>;
    }
    if (log.action === 'import') {
      return <div className="text-xs">Fichier : <b>{d.fichier}</b> · Articles importés : {d.articles_importes}</div>;
    }
    if (log.action === 'snapshot') {
      return <div className="text-xs">Snapshot avec <b>{d.nb_articles}</b> articles</div>;
    }
    if (log.action === 'restore') {
      return <div className="text-xs">Restauration de <b>{d.nb_articles_restaures}</b> articles</div>;
    }
    if (log.action === 'jete_create') {
      return <div className="text-xs">Quantité : <b>{d.quantite} {d.unite}</b> · Raison : {d.raison} · Coût : {d.cout_estime}€</div>;
    }
    return <pre className="text-xs">{JSON.stringify(d, null, 2)}</pre>;
  };

  const actionsList = useMemo(() => (synthese?.actions_disponibles || []).sort(), [synthese]);
  const usersList = useMemo(() => (synthese?.users_disponibles || []).sort(), [synthese]);

  return (
    <div className="space-y-4" data-testid="historique-tab">
      {/* Filtres */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap items-end gap-3">
            <div><Label className="text-xs">Du</Label><Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-40" data-testid="hist-date-from" /></div>
            <div><Label className="text-xs">Au</Label><Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-40" data-testid="hist-date-to" /></div>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" onClick={() => { setDateFrom(daysAgoIso(1)); setDateTo(todayIso()); }}>24h</Button>
              <Button variant="outline" size="sm" onClick={() => { setDateFrom(daysAgoIso(7)); setDateTo(todayIso()); }}>7j</Button>
              <Button variant="outline" size="sm" onClick={() => { setDateFrom(daysAgoIso(30)); setDateTo(todayIso()); }}>30j</Button>
              <Button variant="outline" size="sm" onClick={() => { setDateFrom('2020-01-01'); setDateTo(todayIso()); }}>Tout</Button>
            </div>
            <div>
              <Label className="text-xs">Utilisateur</Label>
              <Select value={userFilter} onValueChange={setUserFilter}>
                <SelectTrigger className="w-48" data-testid="hist-user-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les utilisateurs</SelectItem>
                  {usersList.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Action</Label>
              <Select value={actionFilter} onValueChange={setActionFilter}>
                <SelectTrigger className="w-44" data-testid="hist-action-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les actions</SelectItem>
                  {actionsList.map(a => <SelectItem key={a} value={a}>{ACTION_LABELS[a]?.label || a}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[180px]">
              <Label className="text-xs">Rechercher un article</Label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Nom de l'article…" className="pl-8" data-testid="hist-search" />
              </div>
            </div>
            <Button onClick={fetchData} variant="outline" size="sm"><RefreshCw className="w-4 h-4 mr-1" /> Actualiser</Button>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      {synthese && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card><CardContent className="pt-4"><div className="text-xs text-muted-foreground">Actions totales</div><div className="text-2xl font-bold flex items-center gap-2"><History className="w-5 h-5 text-blue-600" />{synthese.total}</div></CardContent></Card>
          <Card><CardContent className="pt-4"><div className="text-xs text-muted-foreground">Utilisateurs</div><div className="text-2xl font-bold flex items-center gap-2"><User className="w-5 h-5 text-purple-600" />{synthese.par_user.length}</div></CardContent></Card>
          <Card><CardContent className="pt-4"><div className="text-xs text-muted-foreground">Types d'action</div><div className="text-2xl font-bold flex items-center gap-2"><Activity className="w-5 h-5 text-amber-600" />{synthese.par_action.length}</div></CardContent></Card>
          <Card><CardContent className="pt-4"><div className="text-xs text-muted-foreground">Jours actifs</div><div className="text-2xl font-bold flex items-center gap-2"><Calendar className="w-5 h-5 text-green-600" />{synthese.par_jour.length}</div></CardContent></Card>
        </div>
      )}

      {/* Top users / actions */}
      {synthese && synthese.total > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Par utilisateur</CardTitle></CardHeader><CardContent>
            <div className="space-y-1">{synthese.par_user.map(u => (
              <div key={u.user_email} className="flex items-center justify-between text-sm">
                <span className="font-medium">{u.user_email || 'inconnu'}</span>
                <Badge variant="secondary">{u.count}</Badge>
              </div>
            ))}</div>
          </CardContent></Card>
          <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Par type d'action</CardTitle></CardHeader><CardContent>
            <div className="space-y-1">{synthese.par_action.map(a => (
              <div key={a.action} className="flex items-center justify-between text-sm">
                <Badge variant="outline" className={`${ACTION_LABELS[a.action]?.color || ''} text-xs`}>{ACTION_LABELS[a.action]?.label || a.action}</Badge>
                <span className="font-bold tabular-nums">{a.count}</span>
              </div>
            ))}</div>
          </CardContent></Card>
        </div>
      )}

      {/* Table */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Journal détaillé</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
          ) : logs.length === 0 ? (
            <div className="text-center text-sm text-muted-foreground py-6">Aucune action sur cette période.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-6"></TableHead>
                  <TableHead>Date & Heure</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Article</TableHead>
                  <TableHead>Zone</TableHead>
                  <TableHead>Utilisateur</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map(log => {
                  const open = expanded === log.id;
                  const meta = ACTION_LABELS[log.action] || { label: log.action, color: 'bg-gray-100' };
                  return (
                    <Fragment key={log.id}>
                      <TableRow className="cursor-pointer hover:bg-accent/40" onClick={() => setExpanded(open ? null : log.id)}>
                        <TableCell>{open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</TableCell>
                        <TableCell className="text-xs tabular-nums">{fmtDateTime(log.datetime)}</TableCell>
                        <TableCell><Badge variant="outline" className={`${meta.color} text-xs`}>{meta.label}</Badge></TableCell>
                        <TableCell className="text-sm font-medium">{log.item_nom || '—'}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{log.zone}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{log.user_email || '—'}</TableCell>
                      </TableRow>
                      {open && (
                        <TableRow>
                          <TableCell colSpan={6} className="bg-muted/30">
                            {renderDetails(log)}
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
